// src/components/user/controltower/useControlTower.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';
const POLL_MS  = 5000;

export function useControlTower(userId) {
  const [user, setUser]                         = useState({ addresses: [] });
  const [deliveryPoints, setDeliveryPoints]     = useState([]);
  const [expressPoints, setExpressPoints]       = useState([]);
  const [orders, setOrders]                     = useState([]);
  const [drivers, setDrivers]                   = useState([]);
  const [loading, setLoading]                   = useState(true);

  // Selected items
  const [selectedDelivery, setSelectedDelivery] = useState(null); // { type: 'regular'|'express', data }

  // Sidebar
  const [activeTab, setActiveTab]               = useState('regular'); // 'regular' | 'express'
  const [searchQuery, setSearchQuery]           = useState('');
  const [showList, setShowList]                 = useState(true);

  // ── Fetch user data (delivery + express + orders) ────────
  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/user/${userId}`);
      setUser(res.data);
      setDeliveryPoints(res.data.delivery_points         || []);
      setExpressPoints(res.data.express_delivery_points  || []);
      setOrders(res.data.order_points                    || []);
    } catch (err) {
      console.error('Error fetching tower data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // ── Fetch drivers (union of regular + express driver_ids) ─
  useEffect(() => {
    const ids = [
      ...new Set([
        ...deliveryPoints.map(d => d.driver_id).filter(Boolean),
        ...expressPoints.map(d => d.driver_id).filter(Boolean),
        ...orders.map(o => o.driver_id).filter(Boolean),
      ])
    ];
    if (!ids.length) return;

    axios.post(`${BASE_URL}/api/drivers`, { driver_ids: ids })
      .then(res => setDrivers(res.data.drivers || []))
      .catch(err => console.error('Error fetching drivers:', err));

    const id = setInterval(() => {
      axios.post(`${BASE_URL}/api/drivers`, { driver_ids: ids })
        .then(res => setDrivers(res.data.drivers || []))
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(id);
  }, [deliveryPoints, expressPoints, orders]);

  // ── Filtered lists ────────────────────────────────────────
  const filteredRegular = deliveryPoints.filter(d => {
    const q = searchQuery.toLowerCase();
    return (
      d.recipient_name?.toLowerCase().includes(q) ||
      d.recipient_phone?.toLowerCase().includes(q) ||
      String(d.id).includes(q)
    );
  });

  const filteredExpress = expressPoints.filter(d => {
    const q = searchQuery.toLowerCase();
    return (
      d.recipient_name?.toLowerCase().includes(q) ||
      d.recipient_phone?.toLowerCase().includes(q) ||
      d.pickup_address?.toLowerCase().includes(q) ||
      d.dropoff_address?.toLowerCase().includes(q) ||
      String(d.id).includes(q)
    );
  });

  // ── Express driver id set (for marker coloring) ───────────
  const expressDriverIds = new Set(
    expressPoints.map(e => e.driver_id).filter(Boolean)
  );

  return {
    user, deliveryPoints, expressPoints, orders, drivers,
    loading, selectedDelivery, setSelectedDelivery,
    activeTab, setActiveTab,
    searchQuery, setSearchQuery,
    showList, setShowList,
    filteredRegular, filteredExpress,
    expressDriverIds,
  };
}